"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.defaultOrder = defaultOrder;
exports.getSquaredTolerance = getSquaredTolerance;
exports.getTolerance = getTolerance;
exports.renderFeature = renderFeature;

var _BuilderType = _interopRequireDefault(require("../render/canvas/BuilderType.js"));

var _GeometryType = _interopRequireDefault(require("../geom/GeometryType.js"));

var _ImageState = _interopRequireDefault(require("../ImageState.js"));

var _util = require("../util.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/renderer/vector
 */

/**
 * Feature callback. The callback will be called with three arguments. The first
 * argument is one {@link module:ol/Feature~Feature feature} or {@link module:ol/render/Feature~RenderFeature render feature}
 * at the pixel, the second is the {@link module:ol/layer/Layer~Layer layer} of the feature and will be null for
 * unmanaged layers. The third is the {@link module:ol/geom/SimpleGeometry~SimpleGeometry} of the feature. For features
 * with a GeometryCollection geometry, it will be the first detected geometry from the collection.
 * @template T
 * @typedef {function(import("../Feature.js").FeatureLike, import("../layer/Layer.js").default<import("../source/Source").default>, import("../geom/SimpleGeometry.js").default): T} FeatureCallback
 */

/**
 * Tolerance for geometry simplification in device pixels.
 * @type {number}
 */
var SIMPLIFY_TOLERANCE = 0.5;
/**
 * @const
 * @type {Object<import("../geom/GeometryType.js").default,
 *                function(import("../render/canvas/BuilderGroup.js").default, import("../geom/Geometry.js").default,
 *                         import("../style/Style.js").default, Object): void>}
 */

var GEOMETRY_RENDERERS = {
  'Point': renderPointGeometry,
  'LineString': renderLineStringGeometry,
  'Polygon': renderPolygonGeometry,
  'MultiPoint': renderMultiPointGeometry,
  'MultiLineString': renderMultiLineStringGeometry,
  'MultiPolygon': renderMultiPolygonGeometry,
  'GeometryCollection': renderGeometryCollectionGeometry,
  'Circle': renderCircleGeometry
};
/**
 * @param {import("../Feature.js").FeatureLike} feature1 Feature 1.
 * @param {import("../Feature.js").FeatureLike} feature2 Feature 2.
 * @return {number} Order.
 */

function defaultOrder(feature1, feature2) {
  return parseInt((0, _util.getUid)(feature1), 10) - parseInt((0, _util.getUid)(feature2), 10);
}
/**
 * @param {number} resolution Resolution.
 * @param {number} pixelRatio Pixel ratio.
 * @return {number} Squared pixel tolerance.
 */


function getSquaredTolerance(resolution, pixelRatio) {
  var tolerance = getTolerance(resolution, pixelRatio);
  return tolerance * tolerance;
}
/**
 * @param {number} resolution Resolution.
 * @param {number} pixelRatio Pixel ratio.
 * @return {number} Pixel tolerance.
 */


function getTolerance(resolution, pixelRatio) {
  return SIMPLIFY_TOLERANCE * resolution / pixelRatio;
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Builder group.
 * @param {import("../geom/Circle.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").default} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderCircleGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var fillStyle = style.getFill();
  var strokeStyle = style.getStroke();

  if (fillStyle || strokeStyle) {
    var circleReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].CIRCLE);
    circleReplay.setFillStrokeStyle(fillStyle, strokeStyle);
    circleReplay.drawCircle(geometry, feature);
  }

  var textStyle = style.getText();

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} replayGroup Replay group.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../style/Style.js").default} style Style.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {function(import("../events/Event.js").default): void} listener Listener function.
 * @param {import("../proj.js").TransformFunction} [opt_transform] Transform from user to view projection.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 * @return {boolean} `true` if style is loading.
 */


function renderFeature(replayGroup, feature, style, squaredTolerance, listener, opt_transform, opt_declutterBuilderGroup) {
  var loading = false;
  var imageStyle = style.getImage();

  if (imageStyle) {
    var imageState = imageStyle.getImageState();

    if (imageState == _ImageState["default"].LOADED || imageState == _ImageState["default"].ERROR) {
      imageStyle.unlistenImageChange(listener);
    } else {
      if (imageState == _ImageState["default"].IDLE) {
        imageStyle.load();
      }

      imageState = imageStyle.getImageState();
      imageStyle.listenImageChange(listener);
      loading = true;
    }
  }

  renderFeatureInternal(replayGroup, feature, style, squaredTolerance, opt_transform, opt_declutterBuilderGroup);
  return loading;
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} replayGroup Replay group.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../style/Style.js").default} style Style.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {import("../proj.js").TransformFunction} [opt_transform] Optional transform function.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderFeatureInternal(replayGroup, feature, style, squaredTolerance, opt_transform, opt_declutterBuilderGroup) {
  var geometry = style.getGeometryFunction()(feature);

  if (!geometry) {
    return;
  }

  var simplifiedGeometry = geometry.simplifyTransformed(squaredTolerance, opt_transform);
  var renderer = style.getRenderer();

  if (renderer) {
    renderGeometry(replayGroup, simplifiedGeometry, style, feature);
  } else {
    var geometryRenderer = GEOMETRY_RENDERERS[simplifiedGeometry.getType()];
    geometryRenderer(replayGroup, simplifiedGeometry, style, feature, opt_declutterBuilderGroup);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} replayGroup Replay group.
 * @param {import("../geom/Geometry.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 */


function renderGeometry(replayGroup, geometry, style, feature) {
  if (geometry.getType() == _GeometryType["default"].GEOMETRY_COLLECTION) {
    var geometries =
    /** @type {import("../geom/GeometryCollection.js").default} */
    geometry.getGeometries();

    for (var i = 0, ii = geometries.length; i < ii; ++i) {
      renderGeometry(replayGroup, geometries[i], style, feature);
    }

    return;
  }

  var replay = replayGroup.getBuilder(style.getZIndex(), _BuilderType["default"].DEFAULT);
  replay.drawCustom(
  /** @type {import("../geom/SimpleGeometry.js").default} */
  geometry, feature, style.getRenderer(), style.getHitDetectionRenderer());
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} replayGroup Replay group.
 * @param {import("../geom/GeometryCollection.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").default} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderGeometryCollectionGeometry(replayGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var geometries = geometry.getGeometriesArray();
  var i, ii;

  for (i = 0, ii = geometries.length; i < ii; ++i) {
    var geometryRenderer = GEOMETRY_RENDERERS[geometries[i].getType()];
    geometryRenderer(replayGroup, geometries[i], style, feature, opt_declutterBuilderGroup);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/LineString.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderLineStringGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var strokeStyle = style.getStroke();

  if (strokeStyle) {
    var lineStringReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].LINE_STRING);
    lineStringReplay.setFillStrokeStyle(null, strokeStyle);
    lineStringReplay.drawLineString(geometry, feature);
  }

  var textStyle = style.getText();

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/MultiLineString.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderMultiLineStringGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var strokeStyle = style.getStroke();

  if (strokeStyle) {
    var lineStringReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].LINE_STRING);
    lineStringReplay.setFillStrokeStyle(null, strokeStyle);
    lineStringReplay.drawMultiLineString(geometry, feature);
  }

  var textStyle = style.getText();

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/MultiPolygon.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").default} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderMultiPolygonGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var fillStyle = style.getFill();
  var strokeStyle = style.getStroke();

  if (strokeStyle || fillStyle) {
    var polygonReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].POLYGON);
    polygonReplay.setFillStrokeStyle(fillStyle, strokeStyle);
    polygonReplay.drawMultiPolygon(geometry, feature);
  }

  var textStyle = style.getText();

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/Point.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderPointGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var imageStyle = style.getImage();
  var textStyle = style.getText();
  /** @type {import("../render/canvas.js").DeclutterImageWithText} */

  var declutterImageWithText;

  if (opt_declutterBuilderGroup) {
    builderGroup = opt_declutterBuilderGroup;
    declutterImageWithText = imageStyle && textStyle && textStyle.getText() ? {} : undefined;
  }

  if (imageStyle) {
    if (imageStyle.getImageState() != _ImageState["default"].LOADED) {
      return;
    }

    var imageReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].IMAGE);
    imageReplay.setImageStyle(imageStyle, declutterImageWithText);
    imageReplay.drawPoint(geometry, feature);
  }

  if (textStyle && textStyle.getText()) {
    var textReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle, declutterImageWithText);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/MultiPoint.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderMultiPointGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var imageStyle = style.getImage();
  var textStyle = style.getText();
  /** @type {import("../render/canvas.js").DeclutterImageWithText} */

  var declutterImageWithText;

  if (opt_declutterBuilderGroup) {
    builderGroup = opt_declutterBuilderGroup;
    declutterImageWithText = imageStyle && textStyle && textStyle.getText() ? {} : undefined;
  }

  if (imageStyle) {
    if (imageStyle.getImageState() != _ImageState["default"].LOADED) {
      return;
    }

    var imageReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].IMAGE);
    imageReplay.setImageStyle(imageStyle, declutterImageWithText);
    imageReplay.drawMultiPoint(geometry, feature);
  }

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle, declutterImageWithText);
    textReplay.drawText(geometry, feature);
  }
}
/**
 * @param {import("../render/canvas/BuilderGroup.js").default} builderGroup Replay group.
 * @param {import("../geom/Polygon.js").default|import("../render/Feature.js").default} geometry Geometry.
 * @param {import("../style/Style.js").default} style Style.
 * @param {import("../Feature.js").FeatureLike} feature Feature.
 * @param {import("../render/canvas/BuilderGroup.js").default} [opt_declutterBuilderGroup] Builder for decluttering.
 */


function renderPolygonGeometry(builderGroup, geometry, style, feature, opt_declutterBuilderGroup) {
  var fillStyle = style.getFill();
  var strokeStyle = style.getStroke();

  if (fillStyle || strokeStyle) {
    var polygonReplay = builderGroup.getBuilder(style.getZIndex(), _BuilderType["default"].POLYGON);
    polygonReplay.setFillStrokeStyle(fillStyle, strokeStyle);
    polygonReplay.drawPolygon(geometry, feature);
  }

  var textStyle = style.getText();

  if (textStyle && textStyle.getText()) {
    var textReplay = (opt_declutterBuilderGroup || builderGroup).getBuilder(style.getZIndex(), _BuilderType["default"].TEXT);
    textReplay.setTextStyle(textStyle);
    textReplay.drawText(geometry, feature);
  }
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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